import React from "react";
import { ViewBooking } from "collins-js-sdk";
import { VenueConfig } from "../../../../../../../types/venues";
import { WebViewTooltip } from "../../../../SubHeader/WebViewTooltip";

/**
 * Extracts a zone ID from various zone representations.
 * Handles string, number, and object (with `id` property) formats.
 */
export const getZoneId = (zone: unknown): string | null => {
  if (typeof zone === "string" || typeof zone === "number") {
    return String(zone);
  }

  if (zone && typeof zone === "object" && "id" in zone) {
    const zoneId = (zone as { id?: string | number }).id;
    return zoneId !== undefined && zoneId !== null ? String(zoneId) : null;
  }

  return null;
};

/**
 * Renders a comma-separated table string, truncating after `maxItems`
 * and showing a tooltip with the full list when truncated.
 */
export const renderTruncatedTables = (
  tableString: string,
  maxItems: number = 3
) => {
  const tables = tableString.split(", ").map((table) => table.trim());

  if (tables.length <= maxItems) {
    return tableString;
  }

  const visibleTables = tables.slice(0, maxItems);
  const remainingTables = tables.slice(maxItems);
  const remainingCount = remainingTables.length;

  return (
    <>
      {visibleTables.join(", ")}
      <WebViewTooltip
        content={
          <div>
            <strong>All Tables/Areas:</strong>
            <br />
            {tables.join(", ")}
          </div>
        }
        side="top"
      >
        <span className="DiarySidebarListItem__tableTruncation">
          {" "}
          +{remainingCount} more
        </span>
      </WebViewTooltip>
    </>
  );
};

interface UniqueZoneNamesResult {
  uniqueZoneNames: string[];
  zoneNamesFormatted: string;
}

/**
 * Resolves unique zone names from a booking's assigned areas and the
 * venue configuration. Returns both the deduplicated array and a
 * human-readable formatted string (e.g. "Main Floor & Patio").
 */
export const getUniqueZoneNames = (
  booking: ViewBooking,
  venueConfig: VenueConfig
): UniqueZoneNamesResult => {
  const zoneIds = booking.assignedAreas
    ? booking.assignedAreas
        .map((area) => getZoneId(area.zone))
        .filter((zone): zone is string => !!zone)
    : [];

  const zoneNames = zoneIds
    .map((zoneId: string) => {
      const zone = (venueConfig?.bookable_area_zones ?? []).find(
        (bookingZone) => bookingZone.id === zoneId
      );

      return zone?.name ?? "";
    })
    .filter((zoneName): zoneName is string => !!zoneName);

  const uniqueZoneNames = zoneNames.filter(function (
    val: string,
    id: number,
    array: Array<string>
  ) {
    return array.indexOf(val) === id;
  });

  const zoneNamesFormatted =
    uniqueZoneNames.length > 1
      ? uniqueZoneNames
          .slice(0, -1)
          .join(", ")
          .concat(" & ", uniqueZoneNames.slice(-1)[0])
      : (uniqueZoneNames[0] ?? "");

  return { uniqueZoneNames, zoneNamesFormatted };
};
